import { useEffect, useMemo, useRef, useState } from "react";
import { IndicatorsPage } from "./IndicatorsPage";
import { LoginScreen } from "./LoginScreen";
import { navigation, roleLabels, type CurrentSession, type LoginResult, type NotificationCountsData, type Role, type Section } from "./domain";
import { Icon } from "./icons";
import { ModulePage } from "./ModulePage";
import { BRAND_NAME_RU } from "./brand";
import { SESSION_EXPIRED_EVENT, invalidateApiReads, setApiReadScope } from "./apiSession";
import { MANUAL_REFRESH_EVENT, REFRESH_AVAILABILITY_EVENT, manualRefreshBlocked, mountManualRefresh } from "./manualRefresh";
import { NotificationCenter } from "./NotificationCenter";
import { revokeBrowserPushBeforeLogout } from "./browserPush";
import { HASH_NAVIGATION_REQUEST_EVENT, requestNavigationTransition } from "./navigationTransition";
import { GlobalResizableTables } from "./GlobalResizableTables";
import { openingRecoveryPrefix, redactOpeningCommand } from "./openingStock";
import { valuationRecoveryPrefix, redactValuationCommand } from "./stockValuationCommand";
import { serviceRecoveryPrefix, redactServiceCommand } from "./serviceDocumentCommands";
import { moduleUnreadCount } from "./notificationBadges";

type AuthPhase = "loading" | "ready" | "login" | "unavailable";

const sensitiveHandoffKeys = [
  "pharmhub:order-client",
  "pharmhub:execution-request-order",
  "pharmhub:retail-order",
  "pharmhub:payment-basis",
  "pharmhub:supplier-payment-basis",
  "pharmhub:fiscal-sale-recovery",
  "pharmhub:fiscal-shift-recovery",
  "pharmhub:fiscal-x-report-recovery",
  "pharmhub:return-transfer-draft",
] as const;
const sensitiveHandoffPrefixes = [
  "pharmhub:retail-prepayment-command:",
  "pharmhub:retail-draft-cancel:",
  "pharmhub:pos-draft-command:",
  "pharmhub:opening-stock-command:",
  "pharmhub:receiving-correction:",
  "pharmhub:order-number:",
  valuationRecoveryPrefix,
  serviceRecoveryPrefix,
] as const;

function clearSensitiveHandoffs() {
  for (const key of sensitiveHandoffKeys) sessionStorage.removeItem(key);
  for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = sessionStorage.key(index);
    if (key && sensitiveHandoffPrefixes.some((prefix) => key.startsWith(prefix))) {
      if (key.startsWith(openingRecoveryPrefix)) {
        sessionStorage.setItem(key, redactOpeningCommand(sessionStorage.getItem(key) || ""));
      } else if (key.startsWith(valuationRecoveryPrefix)) {
        sessionStorage.setItem(key, redactValuationCommand(sessionStorage.getItem(key) || ""));
      } else if (key.startsWith(serviceRecoveryPrefix)) {
        sessionStorage.setItem(key, redactServiceCommand(sessionStorage.getItem(key) || ""));
      } else sessionStorage.removeItem(key);
    }
  }
}

// Owner-approved workspaces. Keep this matrix explicit so a shared module
// cannot accidentally broaden a role merely by changing its metadata.
const roleSectionOrder: Record<Role, readonly Section[]> = {
  admin: ["dashboard", "audit", "purchases", "sales", "catalog", "clients", "inventory", "finance", "retail", "operator_cash", "tasks", "settings"],
  manager: ["clients", "sales", "catalog", "inventory", "tasks"],
  operator: ["dashboard", "tasks", "purchases", "inventory", "operator_cash", "sales"],
  pharmacy: ["sales", "catalog", "inventory", "pharmacy_money"],
};

interface ShellModule {
  id: string;
  label: string;
  icon: string;
  sections: readonly Section[];
  views?: readonly { label:string; section:Section; view?:string }[];
}

// Desktop modules group only routes that already render a working page. A
// module may expose one or more context tabs, but never a placeholder target.
const roleModules: Record<Role, readonly ShellModule[]> = {
  admin: [
    { id: "indicators", label: "Показатели", icon: "chart", sections: ["dashboard", "audit"], views:[{label:"Показатели",section:"dashboard",view:"metrics"},{label:"Документы",section:"dashboard",view:"documents"},{label:"Корзина",section:"dashboard",view:"trash"},{label:"Аудит",section:"audit",view:"audit"}] },
    { id: "purchases", label: "Закупки", icon: "basket", sections: ["purchases"], views:[{label:"Приёмки",section:"purchases",view:"receipts"},{label:"Физическая приёмка",section:"purchases",view:"physical"},{label:"Контрагенты",section:"purchases",view:"counterparties"},{label:"Возвраты поставщикам",section:"purchases",view:"returns"}] },
    { id: "sales", label: "Продажи", icon: "bag", sections: ["sales"], views:[{label:"Заказы покупателей",section:"sales",view:"orders"},{label:"Счета покупателям",section:"sales",view:"invoices"},{label:"Отгрузки",section:"sales",view:"shipments"},{label:"Прибыльность",section:"sales",view:"profitability"}] },
    { id: "crm", label: "CRM", icon: "users", sections: ["clients"], views:[{label:"Звонки",section:"clients",view:"calls"},{label:"Клиенты",section:"clients",view:"clients"},{label:"Статистика",section:"clients",view:"statistics"},{label:"Контрагенты",section:"clients",view:"counterparties"}] },
    { id: "stock", label: "Склад", icon: "warehouse", sections: ["inventory", "catalog"], views:[{label:"Товары",section:"catalog",view:"products"},{label:"Прайс-листы",section:"catalog",view:"pricelists"},{label:"Обзор",section:"inventory",view:"overview"},{label:"Оприходования",section:"inventory",view:"receipts"},{label:"Списания",section:"inventory",view:"writeoffs"},{label:"Перемещения",section:"inventory",view:"transfers"},{label:"Инвентаризации",section:"inventory",view:"stocktakes"},{label:"Сверка остатков",section:"inventory",view:"reconciliation"}] },
    { id: "money", label: "Деньги", icon: "wallet", sections: ["finance", "operator_cash"], views:[{label:"Касса управляющего",section:"operator_cash"},{label:"Корректировки долга",section:"operator_cash",view:"debt"},{label:"Ежедневные отчёты аптек",section:"finance",view:"daily-reports"},{label:"Платежи",section:"finance",view:"payments"},{label:"Движение денежных средств",section:"finance",view:"cashflow"},{label:"Прибыли и убытки",section:"finance",view:"pnl"},{label:"Взаиморасчёты",section:"finance",view:"settlements"},{label:"Услуги",section:"finance",view:"services"}] },
    { id: "retail", label: "Розница", icon: "store", sections: ["retail"], views:[{label:"Рабочая касса",section:"retail",view:"points"},{label:"Смены",section:"retail",view:"shifts"},{label:"Продажи",section:"retail",view:"sales"},{label:"Возвраты",section:"retail",view:"returns"}] },
    { id: "tasks", label: "Заявки", icon: "check", sections: ["tasks"], views:[{label:"Заявки",section:"tasks",view:"requests"},{label:"Возвраты",section:"tasks",view:"returns"}] },
    { id: "settings", label: "Настройки", icon: "settings", sections: ["settings"], views:[{label:"Компания",section:"settings",view:"company"},{label:"По умолчанию",section:"settings",view:"defaults"},{label:"Доступы",section:"settings",view:"access"},{label:"О системе",section:"settings",view:"system"}] },
  ],
  manager: [
    { id: "crm", label: "CRM", icon: "users", sections: ["clients"], views:[{label:"Звонки",section:"clients",view:"calls"},{label:"Клиенты",section:"clients",view:"clients"},{label:"Статистика",section:"clients",view:"statistics"}] },
    { id: "sales", label: "Продажи", icon: "bag", sections: ["sales"], views:[{label:"Заказы покупателей",section:"sales",view:"orders"},{label:"Счета покупателям",section:"sales",view:"invoices"},{label:"Отгрузки",section:"sales",view:"shipments"}] },
    { id: "products", label: "Товары", icon: "package", sections: ["catalog"], views:[{label:"Товары и остатки",section:"catalog",view:"products"},{label:"Прайс-листы",section:"catalog",view:"pricelists"}] },
    { id: "tasks", label: "Заявки", icon: "check", sections: ["tasks"], views:[{label:"Заявки",section:"tasks",view:"requests"},{label:"Возвраты",section:"tasks",view:"returns"}] },
  ],
  operator: [
    { id: "home", label: "Сводка", icon: "chart", sections: ["dashboard"] },
    { id: "sales", label: "Заказы", icon: "bag", sections: ["sales"], views:[{label:"Заказы покупателей",section:"sales",view:"orders"}] },
    { id: "tasks", label: "Заявки", icon: "check", sections: ["tasks"], views:[{label:"Заявки",section:"tasks",view:"requests"},{label:"Возвраты к приёмке",section:"tasks",view:"returns"}] },
    { id: "receiving", label: "Приёмка", icon: "basket", sections: ["purchases"], views:[{label:"Приёмка",section:"purchases",view:"receipts"}] },
    { id: "stock", label: "Склад", icon: "warehouse", sections: ["inventory"], views:[{label:"Остатки и сроки",section:"inventory",view:"stock"},{label:"Перемещения",section:"inventory",view:"transfers"},{label:"Инвентаризации",section:"inventory",view:"stocktakes"}] },
    { id: "cash", label: "Касса управляющего", icon: "wallet", sections: ["operator_cash"], views:[{label:"Касса управляющего",section:"operator_cash",view:"cash"},{label:"Корректировки долга",section:"operator_cash",view:"debt"},{label:"Услуги",section:"operator_cash",view:"services"}] },
  ],
  pharmacy: [
    { id: "orders", label: "Заказы", icon: "bag", sections: ["sales"], views:[{label:"Заказы",section:"sales",view:"orders"}] },
    { id: "stock", label: "Склад", icon: "package", sections: ["catalog"], views:[{label:"Остатки товара",section:"catalog"}] },
    { id: "receiving", label: "Приёмка", icon: "warehouse", sections: ["inventory"], views:[{label:"Приёмка",section:"inventory",view:"receipts"}] },
    { id: "returns", label: "Возвраты", icon: "arrow", sections: ["inventory"], views:[{label:"Возвраты управляющему",section:"inventory",view:"returns"}] },
    // R-157 plus the latest owner decision: non-fiscal cash documents and
    // reports live separately from the KKT workplace.
    { id: "money", label: "Деньги", icon: "wallet", sections: ["pharmacy_money"], views:[{label:"Передать выручку",section:"pharmacy_money",view:"handovers"},{label:"Расходы аптеки",section:"pharmacy_money",view:"expenses"}] },
    { id: "daily-report", label: "Отчёт", icon: "check", sections: ["pharmacy_money"], views:[{label:"Отчёт за день",section:"pharmacy_money",view:"reports"}] },
  ],
};

function sectionFromHash(): Section {
  const value = window.location.hash.replace("#/", "").split("?", 1)[0] as Section;
  return navigation.some((item) => item.id === value) ? value : "dashboard";
}

function cookie(name: string): string {
  const prefix = `${encodeURIComponent(name)}=`;
  const value = document.cookie.split("; ").find((entry) => entry.startsWith(prefix));
  return value ? decodeURIComponent(value.slice(prefix.length)) : "";
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "PH";
}

function navigationLabel(section: Section, role: Role, fallback: string): string {
  if (role === "manager") {
    if (section === "sales") return "Продажи";
    if (section === "catalog") return "Товары и розничные цены";
  }
  if (role === "operator") {
    if (section === "sales") return "Заказы";
    if (section === "dashboard") return "Сводка";
    if (section === "tasks") return "Заявки";
    if (section === "purchases") return "Приёмка";
    if (section === "inventory") return "Склад";
    if (section === "operator_cash") return "Касса управляющего";
  }
  if (role === "admin" && section === "purchases") return "Закупки и поступления";
  if (role === "admin" && section === "audit") return "Аудит";
  if (role === "admin" && section === "sales") return "Продажи и расходные документы";
  if (role === "admin" && section === "retail") return "Касса аптеки";
  if (role === "admin" && section === "operator_cash") return "Касса управляющего";
  if (role === "admin" && section === "settings") return "Настройки компании и доступов";
  if (role === "pharmacy") {
    if (section === "sales") return "Заказы";
    if (section === "catalog") return "Товары и цены продаж";
    if (section === "inventory") return "Приёмка";
    if (section === "retail") return "Касса и смена";
    if (section === "pharmacy_money") return "Деньги";
  }
  return fallback;
}

export function App() {
  const [authPhase, setAuthPhase] = useState<AuthPhase>("loading");
  const [authRevision, setAuthRevision] = useState(0);
  const [pageRefresh, setPageRefresh] = useState(0);
  const [refreshBlocked, setRefreshBlocked] = useState(manualRefreshBlocked);
  const [session, setSession] = useState<CurrentSession | null>(null);
  const [role, setRole] = useState<Role>("admin");
  const [section, setSection] = useState<Section>(sectionFromHash);
  const [,setRouteRevision]=useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [online, setOnline] = useState(false);
  const [notificationCounts,setNotificationCounts]=useState<NotificationCountsData>({totalUnread:0,totalActionable:0,totalAttention:0,byModule:{}});
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const mobileSidebarRef = useRef<HTMLElement>(null);
  const lastHashRef = useRef(typeof window === "undefined" ? "" : window.location.hash);

  const allowedNavigation = useMemo(() => roleSectionOrder[role].flatMap((id) => {
    const item = navigation.find((candidate) => candidate.id === id);
    return item ? [item] : [];
  }), [role]);

  useEffect(() => {
    const onSessionExpired = () => {
      clearSensitiveHandoffs();
      setApiReadScope(null);
      setSession(null);
      setOnline(false);
      setMenuOpen(false);
      setNotificationCounts({totalUnread:0,totalActionable:0,totalAttention:0,byModule:{}});
      setAuthPhase("login");
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  useEffect(() => mountManualRefresh(window, () => {
    invalidateApiReads();
    setPageRefresh((value) => value + 1);
  }), []);
  useEffect(() => {
    const update = () => setRefreshBlocked(manualRefreshBlocked());
    window.addEventListener(REFRESH_AVAILABILITY_EVENT, update);
    return () => window.removeEventListener(REFRESH_AVAILABILITY_EVENT, update);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setAuthPhase("loading");
    fetch("/api/v1/auth/me", { signal: controller.signal, credentials: "include" })
      .then(async (response) => {
        if (response.status === 401) {
          setApiReadScope(null);
          clearSensitiveHandoffs();
          setSession(null);
          setAuthPhase("login");
          return null;
        }
        if (!response.ok) throw new Error("auth unavailable");
        return response.json() as Promise<CurrentSession>;
      })
      .then((current) => {
        if (!current || controller.signal.aborted) return;
        setApiReadScope(JSON.stringify([current.mode, current.user.id, current.user.role]));
        setSession(current);
        setRole(current.user.role);
        setOnline(true);
        setAuthPhase("ready");
      })
      .catch((reason: unknown) => {
        if (!(reason instanceof DOMException && reason.name === "AbortError")) {
          setOnline(false);
          setAuthPhase("unavailable");
        }
      });
    return () => controller.abort();
  }, [authRevision]);

  useEffect(() => {
    const onHash = () => {setSection(sectionFromHash());setRouteRevision((value)=>value+1);};
    const previousHash = () => lastHashRef.current || "#/dashboard";
    const onHashNavigation = () => {
      const nextHash = window.location.hash;
      const previous = previousHash();
      const proceed = () => {
        lastHashRef.current = nextHash;
        setSection(sectionFromHash());
        setRouteRevision((value) => value + 1);
        if (window.location.hash !== nextHash) window.location.hash = nextHash;
      };
      if (!requestNavigationTransition(proceed, HASH_NAVIGATION_REQUEST_EVENT)) {
        window.history.replaceState(null, "", previous);
      }
    };
    window.addEventListener("hashchange", onHashNavigation);
    return () => window.removeEventListener("hashchange", onHashNavigation);
  }, []);

  useEffect(() => {
    const target = sessionStorage.getItem("pharmhub:registry-focus");
    if (!target) return;
    const focus = () => {
      const element = document.getElementById(target);
      if (!element) return false;
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      sessionStorage.removeItem("pharmhub:registry-focus");
      return true;
    };
    const frame = window.requestAnimationFrame(() => {
      if (!focus()) window.setTimeout(focus, 120);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [section]);

  useEffect(() => {
    if (authPhase === "ready" && !allowedNavigation.some((item) => item.id === section)) {
      navigate(allowedNavigation[0]?.id ?? "dashboard");
    }
  }, [allowedNavigation, authPhase, section]);

  useEffect(() => {
    const sidebar = mobileSidebarRef.current;
    if (!sidebar) return;
    sidebar.inert = !menuOpen;
    if (!menuOpen) return;
    const activeSidebar = sidebar;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => activeSidebar.querySelector<HTMLElement>(".sidebar__close")?.focus({ preventScroll: true }));

    function handleMenuKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        setMenuOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(activeSidebar.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )).filter((element) => !element.hidden && element.getClientRects().length > 0);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) {
        event.preventDefault();
        activeSidebar.focus({ preventScroll: true });
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleMenuKeyDown, true);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", handleMenuKeyDown, true);
      document.body.style.overflow = previousOverflow;
      window.requestAnimationFrame(() => {
        if (!document.querySelector(".document-close-confirm")) {
          mobileMenuButtonRef.current?.focus({ preventScroll: true });
        }
      });
    };
  }, [menuOpen, authPhase]);

  async function login(username: string, password: string) {
    let response: Response;
    try {
      await revokeBrowserPushBeforeLogout();
      response = await fetch("/api/v1/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
    } catch {
      throw new Error("Сервер недоступен. Проверьте соединение.");
    }
    if (response.status === 401) throw new Error("Неверный логин или пароль");
    if (!response.ok) throw new Error("Не удалось выполнить вход");
    const result = await response.json() as LoginResult;
    clearSensitiveHandoffs();
    setApiReadScope(JSON.stringify(["session", result.user.id, result.user.role]));
    setSession({ user: result.user, mode: "session" });
    setRole(result.user.role);
    setOnline(true);
    setAuthPhase("ready");
  }

  async function logout() {
    setApiReadScope(null);
    const csrfToken = cookie("pharmhub_csrf");
    try {
      await revokeBrowserPushBeforeLogout();
      await fetch("/api/v1/auth/logout", {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRF-Token": csrfToken },
      });
    } finally {
      clearSensitiveHandoffs();
      setSession(null);
      setNotificationCounts({totalUnread:0,totalActionable:0,totalAttention:0,byModule:{}});
      setAuthPhase("login");
    }
  }

  function navigate(next: Section,view?:string) {
    const proceed = () => {
      lastHashRef.current = `#/${next}${view?`?view=${encodeURIComponent(view)}`:""}`;
      window.location.hash = `#/${next}${view?`?view=${encodeURIComponent(view)}`:""}`;
      setSection(next);
    };
    setMenuOpen(false);
    requestNavigationTransition(proceed);
  }

  function requestLogout() {
    setMenuOpen(false);
    requestNavigationTransition(() => { void logout(); });
  }

  function changeDemoRole(next: Role) {
    if (session?.mode !== "demo") return;
    setApiReadScope(JSON.stringify(["demo", session.user.id, next]));
    setNotificationCounts({totalUnread:0,totalActionable:0,totalAttention:0,byModule:{}});
    setRole(next);
    setSession({ ...session, user: { ...session.user, role: next } });
  }

  if (authPhase === "loading") {
    return <main className="app-loading"><div className="brand__mark"><span/><span/></div><strong>{BRAND_NAME_RU}</strong><small>Проверяем защищённую сессию…</small></main>;
  }
  if (authPhase === "login" || authPhase === "unavailable") {
    return <LoginScreen mode={authPhase} onLogin={login} onRetry={() => setAuthRevision((value) => value + 1)} />;
  }

  const visibleModules = roleModules[role];
  const currentView = new URLSearchParams(window.location.hash.split("?",2)[1] || "").get("view");
  const activeModule = (role === "pharmacy" && section === "pharmacy_money"
    ? visibleModules.find((item) => item.id === (!currentView || currentView === "reports" || currentView === "shift-reports" || currentView === "daily-reports" ? "daily-report" : "money"))
    : role === "pharmacy" && section === "inventory"
      ? visibleModules.find((item) => item.id === (currentView === "returns" ? "returns" : "receiving"))
      : visibleModules.find((item) => item.sections.includes(section))) ?? visibleModules[0];
  const contextItems:readonly {label:string;section:Section;view?:string}[] = activeModule?.views ?? activeModule?.sections.map((item)=>({label:navigationLabel(item,role,navigation.find((candidate)=>candidate.id===item)?.label??item),section:item})) ?? [];
  const displayName = session?.user.displayName ?? "Пользователь";
  const pageKey = `${session?.user.id}:${role}:${pageRefresh}`;
  const unreadForModule=(item:ShellModule)=>moduleUnreadCount(notificationCounts,item,role==="pharmacy");

  return (
    <div className={`app-shell${role === "pharmacy" ? " pharmacy-workplace" : ""}`}>
      <a className="skip-link" href="#main-workspace">К рабочей области</a>
      <button ref={mobileMenuButtonRef} className="mobile-menu" onClick={() => setMenuOpen(true)} aria-label="Открыть меню" aria-controls="mobile-navigation" aria-expanded={menuOpen}><Icon name="menu" /></button>
      <header className="module-bar">
        <div className="module-brand">
          <div className="brand__mark" aria-hidden="true"><span /><span /></div>
          <div><strong>{BRAND_NAME_RU}</strong><small>управление аптекой</small></div>
        </div>
        <nav className="module-navigation" aria-label="Модули ФармХаб">
          {visibleModules.map((item) => (
            <button
              key={item.id}
              className={activeModule?.id === item.id ? "module-item module-item--active" : "module-item"}
              aria-current={activeModule?.id === item.id ? "page" : undefined}
              onClick={() => {const first=item.views?.[0];navigate(first?.section??item.sections[0]??"dashboard",first?.view);}}
            >
              <Icon name={item.icon} size={19}/><span>{item.label}</span>{unreadForModule(item)>0&&<em className="module-unread-badge">{unreadForModule(item)>99?"99+":unreadForModule(item)}</em>}
            </button>
          ))}
        </nav>
        <div className="module-bar__service" title={online ? "Сервер подключён" : "Нет соединения"}>
          <span className={online ? "status-dot status-dot--online" : "status-dot"}/>
          <span>{online ? "Онлайн" : "Офлайн"}</span>
        </div>
        <button className="shell-refresh" type="button" disabled={refreshBlocked} title={refreshBlocked ? "Дождитесь завершения операции" : "Обновить текущую страницу"} aria-label="Обновить текущую страницу" onClick={() => window.dispatchEvent(new Event(MANUAL_REFRESH_EVENT))}><Icon name="refresh" size={18}/><span>Обновить</span></button>
        <NotificationCenter key={pageKey} allowedSections={allowedNavigation.map((item)=>item.id)} onCounts={setNotificationCounts} sessionUserId={session?.mode==="session"?session.user.id:undefined}/>
        <div className="module-bar__profile">
          <div className={`role-picker ${session?.mode === "demo" ? "role-picker--demo" : ""}`}>
            <span className="avatar">{initials(displayName)}</span>
            <span><strong>{displayName}</strong><small>{roleLabels[role]}</small></span>
            {session?.mode === "demo" && <select value={role} onChange={(event) => changeDemoRole(event.target.value as Role)} aria-label="Демо-роль">{Object.entries(roleLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>}
          </div>
          {session?.mode === "session" && <button className="logout-button" onClick={requestLogout}>Выйти</button>}
        </div>
      </header>
      <aside ref={mobileSidebarRef} id="mobile-navigation" className={`sidebar ${menuOpen ? "sidebar--open" : ""}`} role={menuOpen ? "dialog" : undefined} aria-modal={menuOpen ? "true" : undefined} aria-label="Мобильная навигация" aria-hidden={!menuOpen} tabIndex={-1}>
        <div className="brand">
          <div className="brand__mark" aria-hidden="true"><span /><span /></div>
          <div><strong>{BRAND_NAME_RU}</strong><small>управление аптекой</small></div>
          <button className="sidebar__close" onClick={() => setMenuOpen(false)} aria-label="Закрыть меню"><Icon name="close" /></button>
        </div>
        <nav className="navigation" aria-label="Основная навигация">
          <span className="navigation__caption">Рабочее пространство</span>
          {visibleModules.map((item) => (
            <button key={item.id} className={activeModule?.id === item.id ? "nav-item nav-item--active" : "nav-item"} aria-current={activeModule?.id === item.id ? "page" : undefined} onClick={() => {const first=item.views?.[0];navigate(first?.section??item.sections[0]??"dashboard",first?.view);}}>
              <Icon name={item.icon} /><span>{item.label}</span>{unreadForModule(item)>0&&<em className="nav-unread-badge">{unreadForModule(item)>99?"99+":unreadForModule(item)}</em>}
            </button>
          ))}
        </nav>
        <div className="sidebar__session">
          <span className="avatar">{initials(displayName)}</span>
          <span><strong>{displayName}</strong><small>{roleLabels[role]}</small></span>
          {session?.mode === "session" && <button className="sidebar-logout" onClick={requestLogout}>Выйти</button>}
        </div>
        <div className="sidebar__status">
          <span className={online ? "status-dot status-dot--online" : "status-dot"} />
          <div><strong>{online ? "Сервер подключён" : "Нет соединения"}</strong><small>{session?.mode === "demo" ? "Демонстрационный режим" : "Защищённая сессия"}</small></div>
        </div>
        <button className="sidebar-refresh" type="button" disabled={refreshBlocked} aria-label="Обновить текущую страницу" onClick={() => { setMenuOpen(false); window.dispatchEvent(new Event(MANUAL_REFRESH_EVENT)); }}>↻ Обновить страницу</button>
      </aside>
      {menuOpen && <button className="backdrop" tabIndex={-1} onClick={() => setMenuOpen(false)} aria-label="Закрыть меню" />}

      <main className="main" id="main-workspace" tabIndex={-1}>
        <GlobalResizableTables role={role} userId={session?.user.id ?? "anonymous"} routeKey={`${section}:${new URLSearchParams(window.location.hash.split("?",2)[1]||"").get("view")||""}`}/>
        <nav className="context-tabs" aria-label="Подразделы модуля">
          {contextItems.map((item) => {const activeView=new URLSearchParams(window.location.hash.split("?",2)[1]||"").get("view");const active=section===item.section&&(!item.view||activeView===item.view||(item.view==="reports"&&activeView==="shift-reports"));return <button key={`${item.section}:${item.view||"main"}`} className={active?"context-tab context-tab--active":"context-tab"} aria-current={active?"page":undefined} onClick={()=>navigate(item.section,item.view)}>{item.label}</button>;})}
        </nav>

        <div key={pageKey} className="manual-refresh-page workspace-frame">{section === "dashboard"
          ? (role === "operator"
            ? <ModulePage section="dashboard" role={role} mode={session?.mode} userId={session?.user.id}/>
            : <IndicatorsPage role={role} onNavigate={navigate} displayName={displayName}/>)
          : <ModulePage section={section} role={role} mode={session?.mode} userId={session?.user.id}/>}</div>
      </main>
    </div>
  );
}
